import { Injectable } from '@angular/core';
import { Params, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  CardSearchFilter,
  CardSearchFilterV2,
  LocationType,
  WaypointType,
} from 'src/graphql/generated/graphql.generated';
import { CompanyProps } from 'src/store/generated/company-record.generated';
import {
  NavigableServiceBase,
  NavigationLink,
  NavigationServiceBase,
} from 'src/util/angular/ng-navigation-fx';
import { filterNull } from 'src/util/rxjs/operators/filter-null';
import { TcsConfigurationService } from '../core/configuration.service';
import { CompanyContextService } from './company-context.service';

export class DevKitComponentsNavigation extends NavigableServiceBase {
  constructor(nav: DevKitNavigation, router: Router) {
    super([...nav.commands, 'components'], router, { title: 'Components' });
  }

  get kitchenSink() {
    return this.navigable(['kitchen-sink'], {
      title: 'Kitchen Sink',
    });
  }
}

export class DevKitPatternsNavigation extends NavigableServiceBase {
  constructor(nav: DevKitNavigation, router: Router) {
    super([...nav.commands, 'patterns'], router, { title: 'Patterns' });
  }

  get allPatternsLinks() {
    return [this.navigation, this.layouts];
  }

  get navigation() {
    return this.navigable(['navigation'], {
      title: 'Navigation',
    });
  }

  get layouts() {
    return this.navigable(['layouts'], {
      title: 'Layouts',
    });
  }
}

export class DevKitStyleGuideNavigation extends NavigableServiceBase {
  constructor(nav: DevKitNavigation, router: Router) {
    super([...nav.commands, 'style-guide'], router, { title: 'Style Guide' });
  }

  get allStyleGuideLinks() {
    return [this.colors, this.logo, this.typography, this.utilityClasses];
  }

  get colors() {
    return this.navigable(['colors'], {
      title: 'Colors',
    });
  }

  get logo() {
    return this.navigable(['logo'], {
      title: 'Logo',
    });
  }

  get typography() {
    return this.navigable(['typography'], {
      title: 'Typography',
    });
  }

  get utilityClasses() {
    return this.navigable(['utility-classes'], {
      title: 'Utility Classes',
    });
  }
}

export class DevKitGuidesNavigation extends NavigableServiceBase {
  constructor(nav: DevKitNavigation, router: Router) {
    super([...nav.commands, 'guides'], router, { title: 'Guides' });
  }

  get allGuides() {
    return [
      this.clientSyncGuide,
      this.customIconsGuide,
      this.pageTitlesGuide,
      this.viewModelsGuide,
    ];
  }

  get clientSyncGuide() {
    return this.navigable(['client-sync'], {
      title: 'Client Sync',
    });
  }

  get customIconsGuide() {
    return this.navigable(['custom-icons'], {
      title: 'Custom Icons',
    });
  }

  get pageTitlesGuide() {
    return this.navigable(['page-titles'], {
      title: 'Page Titles',
    });
  }

  get viewModelsGuide() {
    return this.navigable(['view-models']);
  }
}

export class DevKitNavigation extends NavigableServiceBase {
  constructor(router: Router) {
    super(['/dev-kit'], router);
  }

  get devKitLinks() {
    return [
      this.components,
      this.patterns,
      this.icons,
      this.styleGuide,
      this.librariesAndFrameworks,
      this.guides,
    ];
  }

  get components() {
    return new DevKitComponentsNavigation(this, this._router);
  }

  get patterns() {
    return new DevKitPatternsNavigation(this, this._router);
  }

  get icons() {
    return this.navigable(['icons'], {
      title: 'Icons',
    });
  }

  get styleGuide() {
    return new DevKitStyleGuideNavigation(this, this._router);
  }

  get librariesAndFrameworks() {
    return this.navigable(['libs'], {
      title: 'Libraries and Frameworks',
    });
  }

  get guides() {
    return new DevKitGuidesNavigation(this, this._router);
  }
}

export class FbosNavigation extends NavigableServiceBase {
  constructor(airportId: string, airportNav: AirportsNavigation, router: Router) {
    super([...airportNav.commands, airportId, 'fbos'], router);
  }

  get add() {
    return this.navigable(['add']);
  }

  public detail(fboId: string) {
    return this.navigable([fboId]);
  }

  public manage(fboId: string) {
    return this.navigable([fboId, 'edit']);
  }
}

export class AirportsNavigation extends NavigableServiceBase {
  constructor(orgNav: OrganiztionNavigation, router: Router) {
    super([...orgNav.commands, 'airports'], router, {
      title: 'Airports & FBOs',
      icon: 'local_airport',
      description: 'Manage operational targets',
      data: {
        showInOverview: true,
        bgColorClass: 'bgc-amber-A700',
      },
    });
  }

  get add() {
    return this.navigable(['add']);
  }

  public detail(agencyId: string) {
    return this.navigable([agencyId]);
  }

  public manage(agencyId: string) {
    return this.navigable([agencyId, 'manage']);
  }

  public fbo(airportId: string) {
    return new FbosNavigation(airportId, this, this._router);
  }
}

export class AgencyLocationsNavigation extends NavigableServiceBase {
  constructor(agencyId: string, agencyNav: AgenciesNavigation, router: Router) {
    super([...agencyNav.commands, agencyId, 'manage', 'locations'], router);
  }

  get add() {
    return this.navigable(['add']);
  }

  public detail(locationId: string) {
    return this.navigable([locationId]);
  }

  public manage(locationId: string) {
    return this.navigable([locationId, 'edit']);
  }
}
export class AgenciesNavigation extends NavigableServiceBase {
  constructor(orgNav: OrganiztionNavigation, router: Router) {
    super([...orgNav.commands, 'agencies'], router, {
      title: 'Agencies',
      icon: 'domain',
      description: 'Manage external agencies (including competitors)',
    });
  }

  get add() {
    return this.navigable(['add']);
  }

  public detail(agencyId: string) {
    return this.navigable([agencyId]);
  }

  public manage(agencyId: string) {
    return this.navigable([agencyId, 'manage']);
  }

  public agencyLocation(agencyId: string) {
    return new AgencyLocationsNavigation(agencyId, this, this._router);
  }
}

export class DeclineReasonsNavigation extends NavigableServiceBase {
  constructor(
    declineCategoryId: string,
    declineCategoryNav: DeclineReasonsNavigation,
    router: Router,
  ) {
    super([...declineCategoryNav.commands, declineCategoryId, 'decline-reasons'], router);
  }

  get add() {
    return this.navigable(['add']);
  }

  public detail(declineReasonId: string) {
    return this.navigable([declineReasonId]);
  }

  public manage(declineReasonId: string) {
    return this.navigable([declineReasonId, 'edit']);
  }
}
export class DeclineCategoriesNavigation extends NavigableServiceBase {
  constructor(orgNav: OrganiztionNavigation, router: Router) {
    super([...orgNav.commands, 'decline-categories'], router, {
      title: 'Decline Reasons',
      icon: 'domain',
      description: 'Manage decline categories and reasons',
    });
  }

  get add() {
    return this.navigable(['add']);
  }

  public detail(id: string) {
    return this.navigable([id]);
  }

  public manage(id: string) {
    return this.navigable([id, 'manage']);
  }

  public declineReason(declineCategoryId: string) {
    return new DeclineReasonsNavigation(declineCategoryId, this, this._router);
  }
}
export class CancellationReasonsNavigation extends NavigableServiceBase {
  constructor(
    cancellationCategoryId: string,
    cancellationCategoryNav: CancellationReasonsNavigation,
    router: Router,
  ) {
    super(
      [...cancellationCategoryNav.commands, cancellationCategoryId, 'cancellation-reasons'],
      router,
    );
  }

  get add() {
    return this.navigable(['add']);
  }

  public detail(cancellationReasonId: string) {
    return this.navigable([cancellationReasonId]);
  }

  public manage(cancellationReasonId: string) {
    return this.navigable([cancellationReasonId, 'edit']);
  }
}
export class CancellationCategoriesNavigation extends NavigableServiceBase {
  constructor(orgNav: OrganiztionNavigation, router: Router) {
    super([...orgNav.commands, 'cancellation-categories'], router, {
      title: 'Cancellation Reasons',
      icon: 'domain',
      description: 'Manage cancellation categories and reasons',
    });
  }

  get add() {
    return this.navigable(['add']);
  }

  public detail(id: string) {
    return this.navigable([id]);
  }

  public manage(id: string) {
    return this.navigable([id, 'manage']);
  }

  public cancellationReason(cancellationReasonId: string) {
    return new CancellationReasonsNavigation(cancellationReasonId, this, this._router);
  }
}

export class SpecialtyTeamMemberNavigation extends NavigableServiceBase {
  constructor(specialtyTeamId: string, specialtyTeamNav: SpecialtyTeamsNavigation, router: Router) {
    super(
      [...specialtyTeamNav.commands, specialtyTeamId, 'manage', 'specialty-team-members'],
      router,
    );
  }

  get add() {
    return this.navigable(['add']);
  }

  public detail(specialtyTeamMemberId: string) {
    return this.navigable([specialtyTeamMemberId]);
  }

  public manage(specialtyTeamMemberId: string) {
    return this.navigable([specialtyTeamMemberId, 'manage']);
  }
}
export class SpecialtyTeamsNavigation extends NavigableServiceBase {
  constructor(orgNav: OrganiztionNavigation, router: Router) {
    super([...orgNav.commands, 'specialty-teams'], router, {
      title: 'Specialty Teams',
      icon: 'group',
      description: 'Manage specialty teams',
    });
  }

  get add() {
    return this.navigable(['add']);
  }

  public detail(id: string) {
    return this.navigable([id]);
  }

  public manage(id: string) {
    return this.navigable([id, 'manage']);
  }

  public specialtyTeamMember(specialtyTeamId: string) {
    return new SpecialtyTeamMemberNavigation(specialtyTeamId, this, this._router);
  }
}

export class AlertsNavigation extends NavigableServiceBase {
  constructor(orgNav: OrganiztionNavigation, router: Router) {
    super([...orgNav.commands, 'alerts'], router, {
      title: 'Alerts',
      icon: 'warning',
      description: 'Manage alerts and Activation Rules',
      data: {
        showInOverview: true,
      },
    });
  }

  get add() {
    return this.navigable(['add']);
  }

  public detail(id: string) {
    return this.navigable([id]);
  }

  public manage(id: string) {
    return this.navigable([id, 'manage']);
  }
}

export class LandingZoneNavigation extends NavigableServiceBase {
  constructor(orgNav: OrganiztionNavigation, router: Router) {
    super([...orgNav.commands, 'landing-zones'], router, {
      title: 'Landing Zones',
      icon: 'flight_land',
      description: 'Manage landing zones',
    });
  }

  get add() {
    return this.navigable(['add']);
  }

  public detail(id: string) {
    return this.navigable([id]);
  }

  public manage(id: string) {
    return this.navigable([id, 'manage']);
  }
}

export class FirewallRulesNavigation extends NavigableServiceBase {
  constructor(orgNav: OrganiztionNavigation, router: Router) {
    super([...orgNav.commands, 'access-rules'], router, {
      title: 'Access Control',
      icon: 'router',
      description: 'Manage Access Control',
      data: {
        showInOverview: true,
      },
    });
  }

  get add() {
    return this.navigable(['add']);
  }

  public detail(id: string) {
    return this.navigable([id]);
  }

  public manage(id: string) {
    return this.navigable([id, 'manage']);
  }
}
export class TrmUsersNavigation extends NavigableServiceBase {
  constructor(orgNav: OrganiztionNavigation, router: Router) {
    super([...orgNav.commands, 'trm-user'], router, {
      title: 'TRM Users',
      icon: 'recent_actors',
      description: 'Manage TRM User list, etc.',
      data: {
        showInOverview: true,
      },
    });
  }
}
export class MedicalFacilitiesNavigation extends NavigableServiceBase {
  constructor(orgNav: OrganiztionNavigation, router: Router) {
    super([...orgNav.commands, 'medical-facilities'], router, {
      title: 'Medical Facilities',
      icon: 'local_hospital',
      description: 'Manage common data libraries such as facilities, agencies, etc.',
      data: {
        showInOverview: true,
      },
    });
  }

  get add() {
    return this.navigable(['add']);
  }

  public detail(id: string) {
    return this.navigable([id]);
  }

  public manage(id: string) {
    return this.navigable([id, 'manage']);
  }
}

export class MaintenanceFacilitiesNavigation extends NavigableServiceBase {
  constructor(orgNav: OrganiztionNavigation, router: Router) {
    super([...orgNav.commands, 'maintenance-facilities'], router, {
      title: 'Maintenance Facilities',
      icon: 'build',
      data: {
        showInOverview: true,
      },
    });
  }

  get add() {
    return this.navigable(['add']);
  }

  public detail(id: string) {
    return this.navigable([id]);
  }

  public manage(id: string) {
    return this.navigable([id, 'manage']);
  }
}

export class CapabilitiesNavigation extends NavigableServiceBase {
  constructor(orgNav: OrganiztionNavigation, router: Router) {
    super([...orgNav.commands, 'capabilities'], router, {
      title: 'Capabilities',
      icon: 'playlist_add_check',
      description: 'Manage Capabilities',
      data: {
        showInOverview: true,
      },
    });
  }

  get add() {
    return this.navigable(['add']);
  }

  public detail(id: string) {
    return this.navigable([id]);
  }

  public manage(id: string) {
    return this.navigable([id, 'manage']);
  }
}

export type CardSearchQueryParams = Partial<Omit<CardSearchFilter, 'serviceDateRange'>> & {
  page?: number;
  pageSize?: number;
};

export type NewCardSearchQueryParams = Partial<Omit<CardSearchFilterV2, 'serviceDateRange'>> & {
  page?: number;
  pageSize?: number;
};

export interface LocationSearchQueryParams {
  name?: string | null;
  type?: LocationType | null;
  maxDistanceInMiles?: number | null;
  address?: string | null;
  page?: number;
  limit?: number;
}

export interface LandingZoneQueryParams {
  name?: string;
  abbreviation?: string;
  city?: string;
  state?: string;
  county?: string;
  page?: number;
  limit?: number;
}

export interface ProgramQueryParams {
  companyId?: string;
  name?: string;
}

export interface BaseQueryParams {
  companyId?: string;
  name?: string;
}

export interface VehicleQueryParams {
  companyId?: string;
}

export interface RegionGroupQueryParams {
  companyId?: string;
}

export interface RegionsParams {
  companyId?: string;
}

export interface PersonnelQueryParams {
  companyId?: string;
}

export interface NotificationGroupQueryParams {
  companyId?: string;
}

export interface ComplianceQueryParams {
  companyId?: string;
}

export interface CapabilityQueryParams {
  name: string | null;
  page?: number;
  limit?: number;
}

export interface CommunicationCenterQueryParams {
  companyId?: string;
}

export interface ControlZoneQueryParams {
  companyId?: string;
}
export interface QuestionnaireQueryParams {
  companyId?: string;
}

export class OrganiztionNavigation extends NavigableServiceBase {
  constructor(
    private companyCode: string,
    private router: Router,
    private tcsConfig: TcsConfigurationService,
    private companyId?: string,
  ) {
    super(['/', companyCode, 'org'], router, { title: 'Organization' });
  }

  get isCurrentContext() {
    const regex = new RegExp(`/${this.companyCode}/org`);
    return regex.test(this.router.url);
  }

  get orgCompanyLinks(): NavigationLink[] {
    return [
      this.bases(),
      this.vehicles(),
      this.personnel(),
      this.notificationGroups(),
      this.compliance(),
      this.alerts(),
      this.notificationTemplates,
      this.communicationCenters(),
      this.controlZones(),
      this.offerAlgorithms,
      this.firewallRules,
      this.specialtyTeams,
      this.questionnaires(),
      this.trmUsers,
    ];
  }

  get orgLinks(): NavigationLink[] {
    let links = [
      this.overview,
      // this.organization,
      this.maintenanceFacilities,
      this.medicalFacilities,
      this.physicians,
      this.airportsAndFbos,
      this.vehicleModels,
      this.globalSettings,
      this.timestampTypes,
      this.timelines,
      this.agencies(),
      this.landingZones(),
      this.applicationNotifications,
      this.cancellationCategories,
      this.cardReOpenReasons,
      this.declineCategories,
      this.outOfServiceReasons,
      this.nonReferralReasons,
      this.fuelTanks,
      this.capabilities(),
      this.questions,
      this.oktaDirectory,
      this.qarReasons,
      this.diversionReasons,
      this.states,
      this.paipReasons,
      // this.events,
      // this.history,
    ];

    if (this.tcsConfig.value.features.transferCenterEnabled === true) {
      links = [...links, this.transferReasons, this.facilityNonAcceptanceReasons];
    }
    return links;
  }
  // Org Company Links
  public programs(queryParams?: ProgramQueryParams) {
    return this.navigable(['programs'], {
      title: 'Programs',
      icon: 'group',
      description: 'Programs List',
      navigationExtras: {
        queryParams,
      },
    });
  }

  public bases(queryParams?: BaseQueryParams) {
    return this.navigable(['bases'], {
      title: 'Bases',
      icon: 'home',
      description: 'Bases List',
      navigationExtras: {
        queryParams,
      },
    });
  }

  public vehicles(queryParams?: VehicleQueryParams) {
    return this.navigable(['vehicles'], {
      title: 'Vehicles',
      icon: 'flight',
      description: 'Vehicles List',
      navigationExtras: {
        queryParams,
      },
    });
  }

  public regionGroups(queryParams?: RegionGroupQueryParams) {
    return this.navigable(['region-groups'], {
      title: 'Region Groups',
      icon: 'view_list',
      description: 'Region Group List',
      navigationExtras: {
        queryParams,
      },
    });
  }

  public regions(queryParams?: RegionGroupQueryParams) {
    return this.navigable(['regions'], {
      title: 'Regions',
      icon: 'map',
      description: 'Regions List',
      navigationExtras: {
        queryParams,
      },
    });
  }

  public personnel(queryParams?: PersonnelQueryParams) {
    return this.navigable(['people'], {
      title: 'Personnel',
      icon: 'recent_actors',
      description: 'Personnel List',
      navigationExtras: {
        queryParams,
      },
    });
  }

  public notificationGroups(queryParams?: NotificationGroupQueryParams) {
    return this.navigable(['notification-groups'], {
      title: 'Notification Groups',
      icon: 'notifications',
      description: 'Notification Groups List',
      navigationExtras: {
        queryParams,
      },
    });
  }

  public compliance(queryParams?: ComplianceQueryParams) {
    return this.navigable(['compliances'], {
      title: 'Compliance',
      icon: 'playlist_add_check',
      description: 'Compliances List',
      navigationExtras: {
        queryParams,
      },
    });
  }

  public communicationCenters(queryParams?: CommunicationCenterQueryParams) {
    return this.navigable(['communication-centers'], {
      title: 'Communication Centers',
      icon: 'headset_mic',
      description: 'Communication Centers List',
      navigationExtras: {
        queryParams,
      },
    });
  }

  public controlZones(queryParams?: ControlZoneQueryParams) {
    return this.navigable(['control-zones'], {
      title: 'Control Zones',
      icon: 'account_box',
      description: 'Control Zones List',
      navigationExtras: {
        queryParams,
      },
    });
  }

  get specialtyTeams() {
    return new SpecialtyTeamsNavigation(this, this._router);
  }

  // Org Links
  get overview() {
    return this.navigable(['overview'], {
      title: 'Overview',
      icon: 'home',
      description: 'TCS overview',
    });
  }

  get organization() {
    return this.navigable(['organization'], {
      title: 'Organization',
      icon: 'account_balance',
      description: 'Manage your assets and organizational units',
      data: {
        showInOverview: true,
        bgColorClass: 'bgc-light-blue-A700',
      },
    });
  }
  get trmUsers() {
    return new TrmUsersNavigation(this, this._router);
  }
  get medicalFacilities() {
    return new MedicalFacilitiesNavigation(this, this._router);
  }

  get maintenanceFacilities() {
    return new MaintenanceFacilitiesNavigation(this, this._router);
  }

  get airportsAndFbos() {
    return new AirportsNavigation(this, this._router);
  }

  get timestampTypes() {
    return this.navigable(['timestamp-types'], {
      title: 'Timestamp Types',
      icon: 'timer',
      description: 'Manage timestamp types',
      data: {
        showInOverview: true,
      },
    });
  }

  get timelines() {
    return this.navigable(['timelines'], {
      title: 'Timelines',
      icon: 'timeline',
      description: 'Manage timelines',
      data: {
        showInOverview: true,
      },
    });
  }

  get questions() {
    return this.navigable(['questions'], {
      title: 'Questions',
      icon: 'help',
      description: 'Manage questions',
      data: {
        showInOverview: true,
      },
    });
  }

  public questionnaires(queryParams?: QuestionnaireQueryParams) {
    return this.navigable(['questionnaires'], {
      title: 'Questionnaires',
      icon: 'assignment',
      description: 'Manage questionnaires',
      navigationExtras: {
        queryParams,
      },
    });
  }

  get vehicleModels() {
    return this.navigable(['vehicle-models'], {
      title: 'Vehicle Models',
      icon: 'view_list',
      description: 'Manage vehicle models',
    });
  }

  public agencies() {
    return new AgenciesNavigation(this, this._router);
  }

  get events() {
    return this.navigable(['events'], {
      title: 'Events',
      icon: 'event',
      description: 'Manage a timeline of significant business events',
    });
  }
  get history() {
    return this.navigable(['history'], {
      title: 'History',
      icon: 'history',
      description: 'See what’s changed recently',
    });
  }

  get globalSettings() {
    return this.navigable(['global-settings'], {
      title: 'Global Settings',
      icon: 'settings',
      description: 'Manage global settings',
    });
  }

  get applicationNotifications() {
    return this.navigable(['application-notification'], {
      title: 'Application Notifications',
      icon: 'notifications',
      description: 'Manage Application Notifications',
      data: {
        showInOverview: true,
      },
    });
  }

  public landingZones() {
    return new LandingZoneNavigation(this, this._router);
  }

  get cardReOpenReasons() {
    return this.navigable(['card-re-open-reasons'], {
      title: 'Card Re-Open Reasons',
      icon: 'cancel',
      description: 'Manage Card Re-Open reasons',
      data: {
        showInOverview: true,
      },
    });
  }
  get transferCenters() {
    return this.navigable(['transfer-centers'], {
      title: 'Transfer Centers',
      icon: 'location_city',
      description: 'Manage transfer centers',
      data: {
        showInOverview: true,
      },
    });
  }

  get transferReasons() {
    return this.navigable(['transfer-reasons'], {
      title: 'Transfer Reasons',
      icon: 'swap_horiz',
      description: 'Manage transfer reasons',
      data: {
        showInOverview: true,
      },
    });
  }

  get states() {
    return this.navigable(['states'], {
      title: 'States',
      icon: 'map',
      description: 'Manage states',
      data: {
        showInOverview: true,
      },
    });
  }

  get qarReasons() {
    return this.navigable(['qar-reasons'], {
      title: 'QAR Reasons',
      icon: 'domain',
      description: 'Manage QAR reasons',
      data: {
        showInOverview: true,
      },
    });
  }

  get paipReasons() {
    return this.navigable(['paip-reasons'], {
      title: 'PAIP Reasons',
      icon: 'domain',
      description: 'Manage PAIP reasons',
      data: {
        showInOverview: true,
      },
    });
  }

  get diversionReasons() {
    return this.navigable(['diversion-reasons'], {
      title: 'Diversion Reasons',
      icon: 'call_missed_outgoing',
      description: 'Manage diversion reasons',
      data: {
        showInOverview: true,
      },
    });
  }

  get outOfServiceReasons() {
    return this.navigable(['out-of-service-reasons'], {
      title: 'Out of Service Reasons',
      icon: 'pause_circle_filled',
      description: 'Manage out of service reasons',
      data: {
        showInOverview: true,
      },
    });
  }

  public alerts() {
    return new AlertsNavigation(this, this._router);
  }

  get fuelTanks() {
    return this.navigable(['fuel-tanks'], {
      title: 'Fuel Locations',
      icon: 'local_gas_station',
      description: 'Manage fuel Locations',
      data: {
        showInOverview: true,
      },
    });
  }

  get physicians() {
    return this.navigable(['physicians'], {
      title: 'Physicians',
      icon: 'recent_actors',
      description: 'Manage Physicians',
      data: {
        showInOverview: true,
      },
    });
  }

  get notificationTemplates() {
    return this.navigable(['notification-templates'], {
      title: 'Notification Templates',
      icon: 'assignment',
      description: 'Manage notification templates',
      data: {
        showInOverview: true,
      },
    });
  }

  get offerAlgorithms() {
    return this.navigable(['offer-algorithms'], {
      title: 'Transport Offer Algorithms',
      icon: 'list',
      description: 'Manage Transport Offer Algorithms',
      data: {
        showInOverview: true,
      },
    });
  }

  get oktaDirectory() {
    return this.navigable(['okta'], {
      title: 'Okta Directory',
      icon: 'fingerprint',
      description: 'Search Okta Users',
    });
  }

  get firewallRules() {
    return new FirewallRulesNavigation(this, this._router);
  }

  get declineCategories() {
    return new DeclineCategoriesNavigation(this, this._router);
  }

  get cancellationCategories() {
    return new CancellationCategoriesNavigation(this, this._router);
  }

  public capabilities() {
    return new CapabilitiesNavigation(this, this._router);
  }

  get nonReferralReasons() {
    return this.navigable(['non-referral-reasons'], {
      title: 'Non-Referral Reasons',
      icon: 'label_off',
      description: 'Manage Non-Referral Reasons',
      data: {
        showInOverview: true,
      },
    });
  }

  get facilityNonAcceptanceReasons() {
    return this.navigable(['facility-non-acceptance-reasons'], {
      title: 'Facility Non-Acceptance Reasons',
      icon: 'domain_disabled',
      description: 'Manage Facility Non-Acceptance Reasons',
      data: {
        showInOverview: true,
      },
    });
  }
}
export class TripNavigation extends NavigableServiceBase {
  constructor(cardNav: CardNavigation, router: Router) {
    super([...cardNav.commands, 'trip'], router);
  }

  public editWaypoint(waypointId: string) {
    return this.navigable([], {
      navigationExtras: {
        queryParams: {
          waypointId,
          mode: 'edit',
        },
      },
    });
  }

  public insertWaypoint(waypointId: string) {
    return this.navigable([], {
      navigationExtras: {
        queryParams: {
          waypointId,
          mode: 'insert',
        },
      },
    });
  }

  public requestLocation(waypointType: WaypointType) {
    return this.navigable(['request-location'], {
      navigationExtras: {
        queryParams: {
          type: waypointType,
        },
      },
    });
  }
}

export class CardNavigation extends NavigableServiceBase {
  constructor(cardNumber: string, opNav: OperationNavigation, router: Router) {
    super([...opNav.commands, 'cards', cardNumber], router);
  }

  get request() {
    return this.navigable(['request']);
  }

  get assignUnit() {
    return this.navigable(['request', 'assign-unit']);
  }

  get historyNotes() {
    return this.navigable(['history', 'notes']);
  }
  get historyTimes() {
    return this.navigable(['history', 'timeline']);
  }
  get historyTimestamps() {
    return this.navigable(['history', 'timestamps']);
  }
  get trip() {
    return new TripNavigation(this, this._router);
  }
  get questionnaireResponse() {
    return this.navigable(['questionnaire-response']);
  }
  get positionReportHistory() {
    return this.navigable(['position-report']);
  }
}

export class UnitInstanceNavigation extends NavigableServiceBase {
  constructor(unitId: string, nav: UnitNavigation, router: Router) {
    super([...nav.commands, unitId], router);
  }

  get manage() {
    return this.navigable(['manage']);
  }
}

export class UnitNavigation extends NavigableServiceBase {
  constructor(nav: OperationNavigation, router: Router) {
    super([...nav.commands, 'units'], router);
  }

  public add(regionId?: string | undefined) {
    if (regionId != null) {
      return this.navigable(['add'], {
        navigationExtras: { queryParams: { regionId } },
      });
    } else {
      return this.navigable(['add']);
    }
  }

  public detail(unitId: string) {
    return new UnitInstanceNavigation(unitId, this, this._router);
  }
}

export class OperationNavigation extends NavigableServiceBase {
  constructor(private companyCode: string, private router: Router) {
    super(['/', companyCode, 'ops'], router, { title: 'Operations' });
  }

  get isCurrentContext() {
    const regex = new RegExp(`/${this.companyCode}/ops`);
    return regex.test(this.router.url);
  }

  public allRegions(commCenterId?: string | null | undefined) {
    const params: Params = { communicationCenterId: commCenterId };

    return this.navigable(['regions', 'all'], {
      navigationExtras: {
        queryParams: params,
      },
    });
  }

  public allControlZones() {
    return this.navigable(['control-zones']);
  }

  public controlZone(controlZoneId: string) {
    return this.navigable(['control-zones', controlZoneId]);
  }

  public sharedServices() {
    return this.navigable(['regions', 'shared-services']);
  }

  public region(regionId: string) {
    return this.navigable(['regions', regionId]);
  }

  public card(cardNumber: string) {
    return new CardNavigation(cardNumber, this, this._router);
  }

  public locationSearch(queryParams: LocationSearchQueryParams) {
    return this.navigable(['search'], {
      navigationExtras: {
        queryParams,
      },
    });
  }

  get units() {
    return new UnitNavigation(this, this._router);
  }

  public unit(unitId: string) {
    return this.units.detail(unitId);
  }

  public currentCard() {}

  public vehicles(commCenterId?: string | null | undefined) {
    const params: Params = { communicationCenterId: commCenterId };

    return this.navigable(['regions', 'vehicles'], {
      navigationExtras: {
        queryParams: params,
      },
    });
  }
}

export class InsightsNavigation extends NavigableServiceBase {
  constructor(private companyCode: string, private router: Router) {
    super(['/', companyCode, 'insights'], router, { title: 'Insights' });
  }

  get isCurrentContext() {
    const regex = new RegExp(`/${this.companyCode}/insights`);
    return regex.test(this.router.url);
  }

  public cardSearch(queryParams: CardSearchQueryParams) {
    return this.navigable([], {
      navigationExtras: {
        queryParams,
      },
    });
  }

  public oldCardSearch(queryParams: CardSearchQueryParams) {
    return this.navigable(['old'], {
      navigationExtras: {
        queryParams,
      },
    });
  }
}

export class AsbNavigation extends NavigableServiceBase {
  constructor(private companyCode: string, private router: Router) {
    super(['/', companyCode, 'asb'], router, { title: 'Aircraft Status Board' });
  }

  get isCurrentContext() {
    const regex = new RegExp(`/${this.companyCode}/asb`);
    return regex.test(this.router.url);
  }
}

export class OosRumNavigation extends NavigableServiceBase {
  constructor(private companyCode: string, private router: Router) {
    super(['/', companyCode, 'oos-rum'], router, { title: 'OOS Rum' });
  }

  get isCurrentContext() {
    const regex = new RegExp(`/${this.companyCode}/oos-rum`);
    return regex.test(this.router.url);
  }

  public commCenter(commCenterId: string) {
    return this.navigable([commCenterId]);
  }
}

export class RttNavigation extends NavigableServiceBase {
  constructor(private companyCode: string, private router: Router) {
    super(['/', companyCode, 'rtt'], router, { title: 'RTT' });
  }

  get isCurrentContext() {
    const regex = new RegExp(`/${this.companyCode}/rtt`);
    return regex.test(this.router.url);
  }
}

export class RegionNavigation {}

export class CompanyNavigation {
  public org: OrganiztionNavigation;
  public ops: OperationNavigation;
  public insights: InsightsNavigation;
  public asb: AsbNavigation;
  public oosRum: OosRumNavigation;

  constructor(
    companyCode: string,
    router: Router,
    tcsConfig: TcsConfigurationService,
    companyId?: string,
  ) {
    this.ops = new OperationNavigation(companyCode, router);
    this.org = new OrganiztionNavigation(companyCode, router, tcsConfig, companyId);
    this.insights = new InsightsNavigation(companyCode, router);
    this.asb = new AsbNavigation(companyCode, router);
    this.oosRum = new OosRumNavigation(companyCode, router);
  }
}

@Injectable({
  providedIn: 'root',
})
export class TcsNavigationService extends NavigationServiceBase {
  public readonly devKit: DevKitNavigation;
  public currentCompanyNav$: Observable<CompanyNavigation>;
  public currentCompany$: Observable<CompanyProps | undefined>;
  constructor(
    router: Router,
    private companyContextService: CompanyContextService,
    private _tcsConfig: TcsConfigurationService,
  ) {
    super(router);
    this.devKit = new DevKitNavigation(router);
    this.currentCompanyNav$ = this.companyContextService.company$.pipe(
      filterNull(),
      map((c) => new CompanyNavigation(c.companyCode, this._router, _tcsConfig, c.id)),
      filterNull(),
    );
    this.currentCompany$ = this.companyContextService.company$.pipe(map((company) => company));
  }

  public company(companyCode: string, companyId?: string) {
    return new CompanyNavigation(companyCode, this._router, this._tcsConfig, companyId);
  }

  get currentCompany() {
    const companyCode =
      this.companyContextService.company != null
        ? this.companyContextService.company.companyCode
        : null;
    const companyId =
      this.companyContextService.company != null ? this.companyContextService.company.id : null;
    if (companyCode == null) {
      throw new Error(
        'Attempting to get navigation tree for current company code, but none found in context',
      );
    }
    return companyId != null ? this.company(companyCode, companyId) : this.company(companyCode);
  }

  get login() {
    return this.navigable(['/login']);
  }

  get logout() {
    return this.navigable(['/logout']);
  }

  get notFound() {
    return this.navigable(['/not-found'], {
      navigationExtras: {
        skipLocationChange: true,
        replaceUrl: true,
      },
    });
  }

  get fleetstatus() {
    return this.navigable(['/fleet-status']);
  }

  get rtt() {
    return this.navigable(['/rtt']);
  }

  get authenticatedLandingPage() {
    return this.navigable(['/']);
  }
}
